/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../../core/components';
import { Component, TemplateRef, ViewChild } from '@angular/core';
import { ErrorResponseModel } from '../../../../../../core/models';
import { LogService, NotifyService, UserService } from '../../../../../../core/services';
import { NavigationExtras, Router } from '@angular/router';
import { OrganisationsApiService, PortalRbacApiService } from '../../../../shared/http';
import { forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-users-list',
    templateUrl: './portal-users-list.component.html',
    styleUrls: ['./portal-users-list.component.scss']
})

export class PortalUsersListComponent extends BaseComponent {
    /**
     * Reference to the content of this component.
     */
    @ViewChild('drawerContent', { static: true }) public template: TemplateRef<any>;

    /**
     * Page Data
     */
    public data: { users: any, roles: any, groups: any, groupPatch?: any };

    /**
     * Fields to match csv headers
     */
    public submissionFields: any[] = [
        { required: true, value: 'email' },
        { required: false, value: 'disabled' },
        { required: true, value: 'Roles' },
        { required: true, value: 'groups' }
    ];

    /**
     * User button permissions.
     */
    public buttonReference: any = {
        'rbac-import-users': [{ path: '/api/v1/organisations/:id', methods: ['POST'] }],
        'rbac-add-user': [{ path: '/api/v1/organisations/:id', methods: ['POST'] }],
        'rbac-user-edit': [{ path: '/api/v1/organisations/:id', methods: ['PATCH'] }],
        'rbac-user-delete': [{ path: '/api/v1/organisations/:id/:memberid', methods: ['DELETE'] }],

        'rbac-add-role': [{ path: '/api/v1/roles/', methods: ['POST'] }],
        'rbac-role-edit': [{ path: '/api/v1/roles/:id', methods: ['PATCH'] }],
        'rbac-role-duplicate': [{ path: '/api/v1/roles/:id', methods: ['PATCH'] }],
        'rbac-role-delete': [{ path: '/api/v1/roles/:id', methods: ['DELETE'] }],

        'rbac-add-group': [{ path: '/api/v1/groups/', methods: ['POST'] }],
        'rbac-group-edit': [{ path: '/api/v1/groups/:id', methods: ['PATCH'] }],
        'rbac-group-delete': [{ path: '/api/v1/groups/:id', methods: ['DELETE'] }],
    };

    /**
     * Store organisation id.
     * @private
     */
    private organisationId: string;

    constructor(
        private logService: LogService,
        private notifyService: NotifyService,
        private organisationsApiService: OrganisationsApiService,
        private portalRbacApiService: PortalRbacApiService,
        private router: Router,
        private userService: UserService,
    ) {
        super();

        this.setUpPageData();
    }

    /**
     * Import users callback.
     * @param event
     */
    public importUsers(event: any[]): void {
        const navigationExtras: NavigationExtras = { state: { csvData: event } };
        const urlMain: string = '/cm/organisation/users/user/import';
        this.router.navigateByUrl(urlMain, navigationExtras).then().catch();
    }

    /**
     * Redirect to add or edit user page
     * @param id
     */
    public addEditUser(id?: string): void {
        const urlMain: string = '/cm/organisation/users/user';
        const url: string = id ? `${urlMain}/edit/${id}` : urlMain + '/add';
        this.router.navigateByUrl(url).then().catch();
    }

    /**
     * Redirect to add or edit role page
     * @param id
     * @param duplicate
     */
    public addEditRole({ id, duplicate = false }): void {
        const urlMain: string = '/cm/organisation/users/role';
        const url: string = duplicate ? `${urlMain}/edit/${id}/duplicate` : id ? `${urlMain}/edit/${id}` : urlMain + '/add';
        this.router.navigateByUrl(url).then().catch();
    }

    /**
     * Redirect to add or edit group page
     * @param id
     */
    public addEditGroup(id?: string): void {
        const urlMain: string = '/cm/organisation/users/group';
        const url: string = id ? `${urlMain}/edit/${id}` : urlMain + '/add';
        this.router.navigateByUrl(url).then().catch();
    }

    /**
     * Delete user association from organisation.
     * @param user
     */
    public deleteUser(user: any): void {
        const users: any[] = Array.isArray(user) ? user : [user];
        let index: number = 0;
        if (users.length > 1) {
            this.notifyService.muteLocalNotifications();
        }
        for (const user of users) {
            this.organisationsApiService.deleteMemberAssociation(this.organisationId, user.id)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('PortalUsersListComponent: deleteUser', error.message);
                        if (index === users.length - 1) {
                            this.notifyService.unmuteLocalNotifications();
                        }
                    },
                    complete: (): void => {
                        if (index === users.length - 1) {
                            this.setUpPageData();
                            this.notifyService.unmuteLocalNotifications();
                        }
                        index++;
                    }
                });
        }
    }

    /**
     * Delete role.
     * @param role
     */
    public deleteRole(role: any): void {
        this.portalRbacApiService.deleteRole(role.id)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    this.setUpPageData();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('PortalUsersListComponent: deleteRole', error.message);
                }
            });
    }

    /**
     * Delete role.
     * @param group
     */
    public deleteGroup(group: any): void {
        this.portalRbacApiService.deleteGroup(group.id)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    this.setUpPageData();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('PortalUsersListComponent: deleteGroup', error.message);
                }
            });
    }

    /**
     * Set up page data
     * @private
     */
    private setUpPageData(): void {
        this.organisationId = this.userService.getUser('organisationId');

        const apiCalls = {
            users: this.organisationsApiService.getMembers(this.organisationId),
            roles: this.portalRbacApiService.getRoles(),
            groups: this.portalRbacApiService.getGroups()
        };

        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (dataGroups: any): void => {
                    dataGroups['users'] = this.formatUsers(dataGroups);
                    this.data = dataGroups;
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('EnduranceUserListComponent: setUpPageData', error.message);
                }
            });
    }

    /**
     * Format users list
     * @private
     * @param dataGroups
     */
    private formatUsers(dataGroups: any): any[] {
        return dataGroups['users'].map((user: any) => ({
            ...user,
            name: user.activated ? user.firstName + ' ' + user.lastName : 'Pending',
            roles: dataGroups['roles'] ? this.getRBACData(user, dataGroups['roles']) : [],
            groups: dataGroups['groups'] ? this.getRBACData(user, dataGroups['groups']) : []
        }));
    }

    /**
     * Get RBAC data.
     * @param user
     * @param groups
     * @private
     */
    private getRBACData(user: any, groups: any[]): number[] {
        const groupIds: any[] = groups.map(item => {
            if (item.accounts?.includes(user.id?.toString())) {
                return item.id;
            }
        });

        return groupIds.filter((id: string): boolean => id !== undefined);
    }
}
